import Seo from "@/components/Seo";
import Hero from "@/sections/Hero";
import Marquee from "@/components/Marquee";
import SectionHeader from "@/components/SectionHeader";
import ServiceCard from "@/components/ServiceCard";
import ProcessSection from "@/sections/ProcessSection";
import CTASection from "@/components/CTASection";
import Reveal from "@/components/Reveal";
import { LinkButton, ExternalButton } from "@/components/Button";
import CrmDashboard from "@/components/mockups/CrmDashboard";
import EngageDashboard from "@/components/mockups/EngageDashboard";
import { SERVICES, PRODUCTS } from "@/lib/content";
import { Check } from "lucide-react";

const ProductShowcase = ({ product, flip }) => (
  <Reveal className={`grid items-center gap-10 lg:grid-cols-2 ${flip ? "" : ""}`}>
    <div className={flip ? "lg:order-2" : ""}>
      <span className="inline-block rounded-full bg-kryvo-lime/25 px-3.5 py-1.5 text-xs font-semibold text-kryvo-deep" data-testid={`product-badge-${product.slug}`}>
        {product.badge}
      </span>
      <h3 className="mt-4 font-display text-2xl font-semibold text-kryvo-ink sm:text-3xl">
        {product.name}
      </h3>
      <p className="mt-2 text-lg font-medium text-kryvo-green">{product.headline}</p>
      <p className="mt-4 max-w-lg text-base leading-relaxed text-kryvo-ink/65">{product.description}</p>
      <ul className="mt-6 grid max-w-lg grid-cols-1 gap-2.5 sm:grid-cols-2">
        {product.features.slice(0, 4).map((f) => (
          <li key={f.title} className="flex items-center gap-2 text-sm text-kryvo-ink/75">
            <Check className="h-4 w-4 shrink-0 text-kryvo-green" aria-hidden="true" />
            {f.title}
          </li>
        ))}
      </ul>
      <div className="mt-8">
        {product.externalUrl ? (
          <ExternalButton href={product.externalUrl} variant="primary" testId={`product-cta-${product.slug}`}>
            Explore {product.name}
          </ExternalButton>
        ) : (
          <LinkButton to={`/products/${product.slug}`} variant="primary" testId={`product-cta-${product.slug}`}>
            Explore {product.name}
          </LinkButton>
        )}
      </div>
    </div>
    <div className={flip ? "lg:order-1" : ""}>
      <div className="relative">
        <div className="absolute -inset-3 sm:-inset-6 rounded-[2rem] bg-gradient-to-br from-kryvo-lime/20 via-kryvo-mist to-kryvo-green/10" aria-hidden="true" />
        <div className="relative aspect-[4/3]">
          {product.mockup === "crm" ? <CrmDashboard /> : <EngageDashboard />}
        </div>
      </div>
    </div>
  </Reveal>
);

const Home = () => (
  <>
    <Seo
      title="Kryvo Technologies | Digital Product Development & SaaS"
      description="Kryvo Technologies designs and builds modern websites, web applications, AI MVPs and mobile applications, along with SaaS products including Kryvo CRM and Kryvo Engage."
    />
    <Hero />
    <Marquee />

    {/* Services */}
    <section className="container-x py-20 sm:py-28" data-testid="home-services-section">
      <SectionHeader
        eyebrow="Our Services"
        title="Technology Solutions for Your Business"
        description="We design and build modern digital solutions that help businesses launch faster, work smarter and grow stronger."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {SERVICES.map((s, i) => (
          <ServiceCard key={s.slug} service={s} index={i} />
        ))}
      </div>
    </section>

    {/* IT Staffing */}
    <section className="container-x pb-20 sm:pb-28" data-testid="home-staffing-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-kryvo-line bg-gradient-to-br from-kryvo-mist via-white to-kryvo-soft/70 p-8 sm:p-12 lg:p-14">
          <div
            className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-kryvo-lime/20 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="eyebrow" data-testid="home-staffing-eyebrow">
                <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
                IT Staffing
              </span>
              <h2 className="mt-4 text-balance text-3xl font-semibold text-kryvo-ink sm:text-4xl">
                Extend Your Technology Team
              </h2>
              <p className="mt-4 max-w-lg text-base leading-relaxed text-kryvo-sub">
                Scale your engineering capacity with skilled technology professionals and dedicated
                offshore teams — integrated with your processes, tools and goals.
              </p>
              <div className="mt-8">
                <LinkButton to="/staffing" variant="primary" testId="home-staffing-cta">
                  Explore IT Staffing
                </LinkButton>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              {["Dedicated Resources", "Dedicated Teams", "Project-Based Staffing", "Flexible Scaling"].map((m) => (
                <span
                  key={m}
                  className="rounded-full border border-kryvo-line bg-white px-4 py-2.5 text-sm font-medium text-kryvo-ink/75"
                >
                  {m}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>

    {/* Products */}
    <section className="bg-kryvo-mist py-20 sm:py-28" data-testid="home-products-section">
      <div className="container-x">
        <SectionHeader
          eyebrow="Our Products"
          title="SaaS Products for Modern Businesses"
          description="Powerful, easy-to-use products designed to help businesses manage customers, marketing and growth."
        />
        <div className="mt-20 flex flex-col gap-24">
          {PRODUCTS.map((p, i) => (
            <ProductShowcase key={p.slug} product={p} flip={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>

    <ProcessSection />

    {/* About teaser */}
    <section className="container-x pb-20 sm:pb-28" data-testid="home-about-section">
      <Reveal>
        <div className="grid items-center gap-10 rounded-3xl border border-kryvo-green/10 bg-white p-8 shadow-[0_20px_60px_-30px_rgba(6,78,42,0.25)] sm:p-12 lg:grid-cols-2">
          <div>
            <span className="eyebrow">
              <span className="h-px w-6 bg-kryvo-lime" aria-hidden="true" />
              About Kryvo
            </span>
            <h2 className="mt-4 text-balance text-3xl font-semibold text-kryvo-ink sm:text-4xl">
              Technology Built Around Your Business.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-kryvo-ink/65">
              Kryvo Technologies combines strategy, design, engineering and product development to
              transform your ideas, workflows and opportunities into modern digital products.
            </p>
            <div className="mt-8">
              <LinkButton to="/about" variant="outline" testId="home-about-cta">
                More About Us
              </LinkButton>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {["Strategy", "Design", "Engineering", "Product Development"].map((pillar, i) => (
              <div
                key={pillar}
                className={`rounded-2xl border p-6 ${
                  i % 2 === 1
                    ? "translate-y-4 border-kryvo-green bg-kryvo-green text-white"
                    : "border-kryvo-line bg-kryvo-mist text-kryvo-deep"
                }`}
              >
                <span className="font-display text-lg font-semibold">
                  {pillar}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>

    <CTASection />
  </>
);

export default Home;
